/**
 * Utility functions for filtering and handling attachments.
 * Centralizes the logic to determine whether an attachment is already embedded
 * in rich text (HTML/markdown) descriptions or comments.
 */

/**
 * Extracts the URL from an attachment which may be an object, File, or string.
 *
 * @param {Object|string|File} attachment
 * @returns {string|null}
 */
export const getAttachmentUrl = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === 'string') return attachment;
  return attachment.url || attachment.access_url || attachment.download_url || attachment.file_url || attachment.previewUrl || attachment._previewUrl || null;
};

/**
 * Extracts the storage key or identifier from an attachment.
 *
 * @param {Object|string|File} attachment
 * @returns {string|null}
 */
export const getAttachmentKey = (attachment) => {
  if (!attachment || typeof attachment !== 'object') return null;
  return attachment.key || null;
};

/**
 * Checks whether an attachment is already embedded in the given HTML / text content
 * or explicitly marked as an inline attachment.
 *
 * @param {Object|string|File} file - Attachment file object, string URL, or File
 * @param {string} [content=''] - HTML or text content to search within
 * @returns {boolean} True if the attachment is embedded inline or referenced in content
 */
export const isAttachmentAlreadyEmbedded = (file, content = '') => {
  if (!file) return false;

  // 1. Explicit inline / screenshot flags
  if (file._isInline || file._inlineScreenshotId) {
    return true;
  }

  // 2. If content is empty or not a string, it cannot be embedded in content
  if (!content || typeof content !== 'string') {
    return false;
  }

  const trimmedContent = content.trim();
  if (!trimmedContent) return false;

  // 3. Check inline screenshot ID reference in markup
  const screenshotId = typeof file === 'object' ? file._inlineScreenshotId : null;
  if (screenshotId && trimmedContent.includes(screenshotId)) {
    return true;
  }

  // 4. Check temporary preview URL
  const previewUrl = typeof file === 'object' ? (file._previewUrl || file.previewUrl) : null;
  if (previewUrl && trimmedContent.includes(previewUrl)) {
    return true;
  }

  // 5. Check persistent URL (and decoded variant if encoded)
  const url = getAttachmentUrl(file);
  if (url && typeof url === 'string') {
    if (trimmedContent.includes(url)) return true;
    try {
      const decodedUrl = decodeURIComponent(url);
      if (decodedUrl !== url && trimmedContent.includes(decodedUrl)) return true;
    } catch {
      // Ignore URI decode errors
    }
  }

  // 6. Check persistent key (and decoded variant)
  const key = getAttachmentKey(file);
  if (key && typeof key === 'string') {
    if (trimmedContent.includes(key)) return true;
    try {
      const decodedKey = decodeURIComponent(key);
      if (decodedKey !== key && trimmedContent.includes(decodedKey)) return true;
    } catch {
      // Ignore
    }
  }

  return false;
};

/**
 * Alias for isAttachmentAlreadyEmbedded
 */
export const isAttachmentEmbedded = isAttachmentAlreadyEmbedded;

/**
 * Filters out attachments that are embedded in the given HTML / text content
 * or marked as inline.
 *
 * @param {Array} attachments - Array of attachment objects, strings, or Files
 * @param {string} [content=''] - HTML or text content
 * @returns {Array} Attachments that are NOT embedded
 */
export const filterEmbeddedAttachments = (attachments, content = '') => {
  if (!Array.isArray(attachments)) return [];
  return attachments.filter((att) => !isAttachmentAlreadyEmbedded(att, content));
};

/**
 * Constructs the canonical proxy URL for an inline image stored in private blob storage.
 * e.g., "attachments/uuid.png" -> "/api/v1/file/inline/attachments/uuid.png"
 *
 * @param {string} key - Blob storage key
 * @returns {string}
 */
export const buildCanonicalInlineImageUrl = (key) => {
  if (!key || typeof key !== 'string') return '';
  const cleanKey = key.trim().replace(/^\/+/, '');
  if (!cleanKey) return '';
  return `/api/v1/file/inline/${cleanKey.split('/').map(encodeURIComponent).join('/')}`;
};

/**
 * Checks whether a given URL is a canonical inline image proxy URL.
 *
 * @param {string} url
 * @returns {boolean}
 */
export const isCanonicalInlineImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('/file/inline');
};

/**
 * Extracts the storage key from a canonical inline image URL or a direct Azure Blob URL.
 *
 * @param {string} url
 * @returns {string|null}
 */
export const extractKeyFromImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();

  // 1. Match /api/v1/file/inline/<key> or /file/inline/<key>
  const inlineMatch = trimmed.match(/\/file\/inline\/(.+?)(?:\?.*)?$/);
  if (inlineMatch) {
    try {
      return decodeURIComponent(inlineMatch[1]);
    } catch {
      return inlineMatch[1];
    }
  }

  // 2. Match direct Azure Blob Storage URL:
  // e.g. https://storage.blob.core.windows.net/container/attachments/uuid.png?sv=...
  const azureMatch = trimmed.match(/blob\.core\.windows\.net\/[^/]+\/([^?]+)/);
  if (azureMatch) {
    try {
      return decodeURIComponent(azureMatch[1]);
    } catch {
      return azureMatch[1];
    }
  }

  return null;
};

/**
 * Resolves the API origin for routing asset requests to the backend server.
 * In development, frontend is typically on localhost:3000 while API is on localhost:8000.
 *
 * @returns {string} Origin (e.g. 'http://localhost:8000') or empty string
 */
export const getApiOrigin = () => {
  // 1. Explicit API base URL from env
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && typeof envBase === 'string' && envBase.startsWith('http')) {
    try {
      return new URL(envBase).origin;
    } catch {
      // Ignore
    }
  }

  // 2. Dev proxy target from env
  const proxyTarget = process.env.REACT_APP_DEV_PROXY_TARGET;
  if (proxyTarget && typeof proxyTarget === 'string' && proxyTarget.startsWith('http')) {
    try {
      return new URL(proxyTarget).origin;
    } catch {
      // Ignore
    }
  }

  // 3. Fallback when running in browser on localhost:3000
  if (typeof window !== 'undefined' && window.location) {
    const { hostname, port } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (port === '3000') {
        return 'http://localhost:8000';
      }
    }
  }

  return '';
};

/**
 * Resolves an inline image URL for browser rendering.
 * If the URL is a relative canonical URL (/api/v1/file/inline/...) or starts with
 * the dev frontend origin (e.g. localhost:3000) and the API runs on localhost:8000,
 * prefixes or replaces with the correct API origin.
 *
 * @param {string} url
 * @returns {string}
 */
export const resolveInlineImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  // Data URIs and in-memory blob URLs are self-contained and should not be modified
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }

  // Extract path from /api/v1/file/inline/<subpath> or /file/inline/<subpath>
  const match = trimmed.match(/(?:\/api\/v1)?\/file\/inline\/(.+)$/);
  if (!match) {
    return trimmed;
  }

  const cleanSubPath = match[1];
  const normalizedCanonical = `/api/v1/file/inline/${cleanSubPath}`;
  const apiOrigin = getApiOrigin();

  if (apiOrigin) {
    if (trimmed.startsWith(apiOrigin)) {
      return trimmed;
    }
    return `${apiOrigin}${normalizedCanonical}`;
  }

  return normalizedCanonical;
};

/**
 * Converts any resolved or full-origin inline proxy URL back to a pure domain-independent
 * canonical relative URL (/api/v1/file/inline/...) for safe persistence in storage or DB.
 *
 * @param {string} url
 * @returns {string}
 */
export const toCanonicalInlineImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  const match = trimmed.match(/(?:\/api\/v1)?\/file\/inline\/(.+)$/);
  if (match) {
    return `/api/v1/file/inline/${match[1]}`;
  }
  return trimmed;
};

// Global in-memory cache for inline image blob URLs (keyed by canonical relative URL, storage key, or direct URL)
const inlineImageBlobCache = new Map();
const pendingInlineFetches = new Map();

/**
 * Caches an in-memory blob URL for an inline image key, canonical path, or direct URL.
 *
 * @param {string} keyOrUrl
 * @param {string} blobUrl
 */
export const setCachedInlineBlobUrl = (keyOrUrl, blobUrl) => {
  if (!keyOrUrl || !blobUrl || typeof keyOrUrl !== 'string' || typeof blobUrl !== 'string') return;
  inlineImageBlobCache.set(keyOrUrl, blobUrl);
  const canonical = toCanonicalInlineImageUrl(keyOrUrl);
  if (canonical) {
    inlineImageBlobCache.set(canonical, blobUrl);
  }
};

/**
 * Retrieves a cached in-memory blob URL for a key or URL if available.
 *
 * @param {string} keyOrUrl
 * @returns {string|null}
 */
export const getCachedInlineBlobUrl = (keyOrUrl) => {
  if (!keyOrUrl || typeof keyOrUrl !== 'string') return null;
  if (inlineImageBlobCache.has(keyOrUrl)) {
    return inlineImageBlobCache.get(keyOrUrl);
  }
  const canonical = toCanonicalInlineImageUrl(keyOrUrl);
  if (canonical && inlineImageBlobCache.has(canonical)) {
    return inlineImageBlobCache.get(canonical);
  }
  return null;
};

/**
 * Clears the in-memory inline image blob cache (used mainly in tests).
 */
export const clearCachedInlineBlobUrls = () => {
  inlineImageBlobCache.clear();
  pendingInlineFetches.clear();
};

/**
 * Loads an inline image as a blob URL via CORS fetch to call the backend streaming endpoint
 * (router.get('/inline/*')) while avoiding browser CORP/CORS blocking and HTML fallback responses.
 *
 * @param {string} url
 * @returns {Promise<string>} Blob URL or resolved network URL
 */
export const loadInlineImageBlob = async (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    return trimmed;
  }

  const cached = getCachedInlineBlobUrl(trimmed);
  if (cached) return cached;

  const canonical = toCanonicalInlineImageUrl(trimmed);
  if (canonical && pendingInlineFetches.has(canonical)) {
    return pendingInlineFetches.get(canonical);
  }
  if (pendingInlineFetches.has(trimmed)) {
    return pendingInlineFetches.get(trimmed);
  }

  const targetUrl = resolveInlineImageUrl(trimmed);
  if (!targetUrl) return '';

  const fetchPromise = (async () => {
    try {
      const response = await fetch(targetUrl, {
        method: 'GET',
        mode: 'cors'
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch inline image: ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      setCachedInlineBlobUrl(trimmed, objectUrl);
      setCachedInlineBlobUrl(targetUrl, objectUrl);
      if (canonical) {
        setCachedInlineBlobUrl(canonical, objectUrl);
      }
      return objectUrl;
    } catch (err) {
      console.warn('Failed to load inline image blob via fetch:', err);
      return targetUrl;
    } finally {
      pendingInlineFetches.delete(trimmed);
      if (canonical) pendingInlineFetches.delete(canonical);
    }
  })();

  pendingInlineFetches.set(trimmed, fetchPromise);
  if (canonical) pendingInlineFetches.set(canonical, fetchPromise);

  return fetchPromise;
};

/**
 * Traverses a container element and hydrates all inline image elements:
 * uses cached in-memory blob URLs if available, or fetches from the backend endpoint.
 *
 * @param {HTMLElement} rootElement
 */
export const hydrateInlineImages = (rootElement) => {
  if (!rootElement || typeof rootElement.querySelectorAll !== 'function') return;

  rootElement.querySelectorAll('img').forEach((img) => {
    const rawSrc = img.getAttribute('src') || '';
    const uploadedUrl = img.getAttribute('data-uploaded-url') || '';
    const fileKey = img.getAttribute('data-file-key') || '';

    if (rawSrc.startsWith('blob:') || rawSrc.startsWith('data:')) {
      img.style.opacity = '1';
      return;
    }

    const candidateUrl = rawSrc || uploadedUrl || (fileKey ? buildCanonicalInlineImageUrl(fileKey) : '');
    if (!candidateUrl) return;

    const wrap = img.closest ? img.closest('.pmpi-screenshot-image-wrap') : null;

    const markLoaded = (blobUrl) => {
      if (blobUrl && img.src !== blobUrl) {
        img.src = blobUrl;
      }
      img.style.opacity = '1';
      if (wrap) wrap.classList.remove('is-loading');
    };

    // 1. Check in-memory cache for immediate synchronous render
    const cached = getCachedInlineBlobUrl(candidateUrl) ||
                   getCachedInlineBlobUrl(uploadedUrl) ||
                   (fileKey ? getCachedInlineBlobUrl(fileKey) : null);

    if (cached) {
      if (img.src !== cached) {
        img.src = cached;
      }
      img.style.opacity = '1';
      if (wrap) wrap.classList.remove('is-loading');
      return;
    }

    // While loading, hide the raw broken alt text flash and show loading skeleton
    img.style.opacity = '0';
    img.style.transition = 'opacity 0.2s ease-in-out';
    if (wrap) wrap.classList.add('is-loading');

    img.onload = () => {
      img.style.opacity = '1';
      if (wrap) wrap.classList.remove('is-loading');
    };

    // 2. Load via fetch to hit backend streamInlineFile endpoint and avoid CORP blocks
    loadInlineImageBlob(candidateUrl).then((blobUrl) => {
      markLoaded(blobUrl);
    });

    // 3. Fallback on error
    img.onerror = () => {
      loadInlineImageBlob(candidateUrl).then((blobUrl) => {
        markLoaded(blobUrl);
      });
    };
  });
};




